/**
 * ALTIORA · AdminPressKit — Générateur de kit presse sur-mesure.
 *
 * Interface interne pour composer un lien de kit presse personnalisé par
 * prospect (nom + logo optionnel). Le fondateur peut ensuite copier
 * l'URL et l'envoyer par email, ou télécharger le PDF directement.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Copy, Sparkles, FileText } from "lucide-react";
import {
  PageShell, PageHeader, Card, Section, PrimaryButton, GhostButton,
  FormField, Input, Alert,
  NAVY, CHAMPAGNE, GOLD, IVORY, SLATE,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";

export default function AdminPressKit() {
  const [prospect, setProspect] = useState("");
  const [logoUrl, setLogoUrl] = useState("");

  const url = useMemo(() => {
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/manifeste/press-kit.pdf`;
    const p = new URLSearchParams();
    if (prospect.trim()) p.set("prospect", prospect.trim());
    if (logoUrl.trim()) p.set("logo_url", logoUrl.trim());
    const qs = p.toString();
    return qs ? `${base}?${qs}` : base;
  }, [prospect, logoUrl]);

  const publicUrl = useMemo(() => {
    // Lien public partageable (même URL, utile pour email)
    return url;
  }, [url]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Lien copié — prêt à coller dans un email.");
    } catch {
      toast.error("Copie impossible — sélectionne et copie manuellement.");
    }
  };

  return (
    <PageShell testid="admin-press-kit-root">
      <TopBar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <PageHeader
          eyebrow="ALTIORA · Administration"
          title="Kit presse sur-mesure"
          subtitle="Génère un one-pager PDF personnalisé par prospect — nom en tête de page + logo à droite du header. Copie le lien pour l'envoyer par email, ou télécharge directement."
          testid="admin-press-kit-header"
        />

        <Alert variant="info" title="Comment ça marche" testid="admin-press-kit-alert">
          Le PDF est généré à la volée à chaque téléchargement. Le lien
          personnalisé reste public — inclus-le tel quel dans un email
          ciblé, chaque destinataire recevra son propre PDF.
        </Alert>

        <Card className="p-6 mt-4">
          <Section icon={Sparkles} title="Personnalisation">
            <div className="grid grid-cols-1 gap-4 mt-3">
              <FormField label="Nom du prospect (optionnel)" testid="admin-pk-ff-prospect">
                {(a) => (
                  <Input
                    testid="admin-pk-input-prospect"
                    placeholder="Cabinet Dubois & Associés"
                    value={prospect}
                    maxLength={60}
                    onChange={(e) => setProspect(e.target.value)}
                    {...a}
                  />
                )}
              </FormField>
              <FormField
                label="URL du logo (optionnel — PNG/JPG absolute, max 2 MB)"
                testid="admin-pk-ff-logo"
              >
                {(a) => (
                  <Input
                    testid="admin-pk-input-logo"
                    placeholder="https://cabinet-dubois.com/logo.png"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    {...a}
                  />
                )}
              </FormField>
            </div>
          </Section>
        </Card>

        <Card className="p-6 mt-4">
          <Section icon={FileText} title="Lien généré">
            <div className="mt-3 rounded-md border p-3 font-mono text-[11px] break-all"
                 style={{ borderColor: `${CHAMPAGNE}66`, background: IVORY, color: NAVY }}
                 data-testid="admin-pk-url">
              {publicUrl}
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <PrimaryButton onClick={copyLink} testid="admin-pk-copy">
                <Copy size={13} /> Copier le lien
              </PrimaryButton>
              <a href={publicUrl} target="_blank" rel="noopener noreferrer"
                 data-testid="admin-pk-download"
                 className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-[13px] font-medium"
                 style={{ border: `1px solid ${CHAMPAGNE}66`, color: NAVY }}>
                <Download size={13} /> Télécharger le PDF
              </a>
              <GhostButton
                onClick={() => { setProspect(""); setLogoUrl(""); }}
                testid="admin-pk-reset"
              >
                Réinitialiser
              </GhostButton>
            </div>
            <div className="mt-4 text-[11px]" style={{ color: SLATE }}>
              Astuce · si le logo distant n'est pas accessible ou trop lourd (>2 MB),
              le PDF est généré sans logo — le nom du prospect reste imprimé.
            </div>
          </Section>
        </Card>
      </div>
    </PageShell>
  );
}
